import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useMultiSelect } from '@chilecompra/react-kit'
import { DataTable, MultiSelect, Select, Typography, useSelect } from '@chilecompra/react-kit/components'
import generateYearsBetween from '../../modules/utils/years'
import { formatAmount, formatPercentage } from '../../modules/utils/formatters'
import exportToImage, { exportToCsv } from '../../modules/utils/exports'
import { onTradedAmountThunk } from './TradedAmounts.actions'
import useLineChart from '../../components/Charts/LineChart.hooks'
import IconButton from '../../components/Buttons/IconButton'
import Infobar from '../../components/Infobar/Infobar'
import InfoLabel from '../../components/Labels/InfoLabel'
import LineChart from '../../components/Charts/LineChart'
import TradedAmountsNoInfo from '../../components/Errors/TradedAmountsNoInfo'
import FichaTradedAmounts from '../../components/Infographics/FichaTradedAmounts'
import { Wrapper, GridWrapper, Link } from './TradedAmounts.styles'

/**
 * The TradedAmounts' container.
 */
const TradedAmounts = () => {
  const dispatch = useDispatch()
  const { latestUpdate } = useSelector(state => state.topNavbar)
  const { idEntity } = useSelector(state => state.buyerSection)
  const { endYear, loading: initialLoading, startYear } = useSelector(state => state.topNavbar)
  const years = generateYearsBetween(endYear, startYear)
  const inputLineChartRef = useRef()
  const dataTableRef = useRef()
  const [showTable, setShowTable] = useState(false)
  const [expandArrow, setExpandArrow] = useState(false)
  const [fileName, setFileName] = useState(`Montos_Transados_${years[0].value}`)
  const [chartTotalAmount, setChartTotalAmount] = useState(0)

  const {
    data: { chartStructure, csvStructure },
    fetching: loading
  } = useSelector(state => state.tradedAmount)
  const {
    values: yearsValue,
    onChange: handleYearsChange,
    onDelete: handleYearsDelete
  } = useMultiSelect({
    initialValues: [years[0].value],
    min: 1,
    max: 4,
    changeCallback: updatedValue => {
      dataTableRef.current?.setPage(1)
      dispatch(onTradedAmountThunk({ idEntity, years: updatedValue }))
      setFileName('Montos_Transados_' + updatedValue)
    }
  })
  const { value: distValue, onChange: handleDistribution } = useSelect({ initialValue: 'amount' })
  const { serie: info, category: categories } = useLineChart(chartStructure, distValue)
  const distribution = [
    { name: 'Mes del año', value: 'amount' },
    { name: 'Acumulativo anual', value: 'totalAmount' },
    { name: 'Monto Porcentual Mensual', value: 'percentageMonth' },
    { name: 'Monto Porcentual Acumulado', value: 'percentageAccumulative' }
  ]

  const handleShowTable = updatedValue => setShowTable(updatedValue)

  const handleCaptureImage = () => exportToImage('TradedAmounts', fileName)
  const handleDownloadFile = () => exportToCsv(csvStructure, fileName)

  useEffect(() => {
    if (!idEntity || initialLoading) {
      return
    }

    dispatch(onTradedAmountThunk({ idEntity, years: [years[0].value] }))
  }, [initialLoading, idEntity])

  useEffect(() => {
    if (chartStructure[0] !== undefined) {
      const amountsArray = []
      chartStructure.forEach(function (year) {
        const yearAmounts = year.amount.reduce((a, b) => a + b, 0)
        amountsArray.push(yearAmounts)
      })
      const totalAmount = amountsArray.reduce((a, b) => a + b, 0)
      setChartTotalAmount(totalAmount)
    }
  }, [chartStructure])

  return (
    <Wrapper>
      <GridWrapper container justifyContent="center">
        <GridWrapper item xs={12} md={10}>
          <Typography variant="h2" fontSize="22px" fontWeight="bold">
            Montos transados
          </Typography>
          <Typography variant="body2" fontSize="16px" lineHeight="20px" margin="16px 0 48px">
            Conoce el comportamiento y evolución de los montos transados por el organismo comprador durante el o los
            años seleccionados. Recuerda que estos montos se extraen de la orden de compra que emite una entidad en{' '}
            <Link href="https://www.mercadopublico.cl/Home">www.mercadopublico.cl</Link>, corresponden a compromisos de
            los organismos, no a pagos.
            <br />
            <br />
            Puedes seleccionar hasta un máximo de 4 años al mismo tiempo.
            <br />
            <br />
            La información de montos transados dentro de esta sección corresponde al monto bruto (monto neto +
            impuestos).
          </Typography>
        </GridWrapper>
      </GridWrapper>
      <GridWrapper container alignItems="center" justifyContent="center">
        <GridWrapper container item align="left" md={8} spacing={1} xs={12}>
          <GridWrapper item align="left" md={3} xs={12}>
            <MultiSelect
              label="AÑO"
              margin="0 50px 0 0"
              options={years}
              values={yearsValue}
              onChange={handleYearsChange}
              onDelete={handleYearsDelete}
            />
          </GridWrapper>
          <GridWrapper item align="left" md={5} xs={12}>
            <Select label="DISTRIBUCIÓN" options={distribution} value={distValue} onChange={handleDistribution} />
          </GridWrapper>
        </GridWrapper>
        <GridWrapper item align="right" md={2} xs={12} margin="6px 0 0">
          <IconButton
            singleIcon
            disabled={!showTable}
            margin="0 16px 0 0"
            type="toggleChart"
            width="40px"
            variant="outlined"
            onClick={() => handleShowTable(false)}
          />
          <IconButton
            singleIcon
            disabled={showTable}
            type="toggleTable"
            margin="0 0 0 16px"
            variant="outlined"
            onClick={() => handleShowTable(true)}
          />
        </GridWrapper>
      </GridWrapper>
      <GridWrapper container justifyContent="center">
        {showTable ? (
          <GridWrapper item xs={12} md={10}>
            <DataTable
              enableResults
              headers={[
                { label: 'Año', value: 0 },
                { label: 'Mes', value: 1 },
                { label: 'Monto por mes', value: 2 },
                { label: 'Monto acumulado', value: 3 },
                { label: 'Monto porcentual mensual', value: 4 },
                { label: 'Monto porcentual acumulado', value: 5 }
              ]}
              headersCellsProps={[
                { enableFilter: true, enableSort: true },
                { enableFilter: true, enableSort: true },
                { enableFilter: true, enableSort: true },
                { enableFilter: true, enableSort: true },
                { enableFilter: true, enableSort: true },
                { enableFilter: true, enableSort: true }
              ]}
              initialRows={6}
              loading={loading}
              records={csvStructure.slice(1)}
              recordsDecorators={[
                ([year]) => year,
                ([, month]) => month,
                ([, , amount]) => `$${formatAmount(amount)}`,
                ([, , , amount]) => `$${formatAmount(amount)}`,
                ([, , , , percentage]) => formatPercentage(percentage),
                ([, , , , , percentage]) => formatPercentage(percentage)
              ]}
              ref={dataTableRef}
            />
          </GridWrapper>
        ) : chartTotalAmount >= 1 ? (
          <GridWrapper item xs={12} md={10} margin="24px 0 0 0">
            <InfoLabel label="Millones de pesos (CLP)" margin="0 0 40px 0" />
            <LineChart
              categories={categories}
              distribution={distValue}
              fileName={fileName}
              id="TradedAmounts"
              info={info}
              inputLineChartRef={inputLineChartRef}
              year={yearsValue?.join(', ')}
            />
          </GridWrapper>
        ) : (
          <GridWrapper item xs={12} md={10} margin="8px 0 0 0">
            <TradedAmountsNoInfo />
          </GridWrapper>
        )}
      </GridWrapper>
      <GridWrapper container justifyContent="center" margin="32px 0 0 0">
        <GridWrapper item xs={12} md={10}>
          <Infobar
            disableDownloadChart={showTable}
            disableDownloadCsv={!showTable}
            disableInfographic={showTable}
            expandArrow={expandArrow}
            infographic={<FichaTradedAmounts />}
            latestUpdate={latestUpdate}
            onCaptureImage={handleCaptureImage}
            onDownloadFile={handleDownloadFile}
            setExpandArrow={setExpandArrow}
          />
        </GridWrapper>
      </GridWrapper>
    </Wrapper>
  )
}

export default TradedAmounts
